/**
 * One company as a small dossier, not a tile with a logo (§10.2).
 */
import { Link } from 'wouter'
import type { CompanyCard } from '@server/sql/companies.ts'
import { Bar, bandOf } from '~/components/ui/primitives.tsx'
import { Raw } from '~/components/ui/raw.tsx'
import { useT } from '~/i18n/index.tsx'
import { count, percent0 } from '~/i18n/format.ts'
import { cn } from '~/lib/cn.ts'
import { useSuperadmin } from '~/lib/superadmin.ts'
import { CoverageMatrix } from './coverage.tsx'

export function CompanyGridCard({ c }: { c: CompanyCard }) {
  const { t, tn, locale, v } = useT()
  const superadmin = useSuperadmin()
  const band = bandOf(c.quality.passRate)

  return (
    <article className="flex flex-col rounded-[var(--radius-container)] border border-[var(--border)] bg-[var(--bg-raised)] transition-colors duration-75 hover:border-[var(--border-strong)] focus-within:border-[var(--border-strong)]">
      {/* --- identity ------------------------------------------------- */}
      <Link
        href={`/c/${c.symbol}`}
        className="group block border-b border-[var(--border)] px-4 py-3 transition-colors duration-75 active:bg-[var(--bg-sunken)]"
      >
        <div className="flex items-baseline gap-2">
          <span className="tnum font-[family-name:var(--font-mono)] text-[length:var(--text-tiny)] text-[var(--text-muted)]">
            {c.symbol}
          </span>
          <span className="min-w-0 flex-1 truncate text-[length:var(--text-prose)] font-medium text-[var(--text)] group-hover:text-[var(--accent)]">
            {c.nameEn ?? c.symbol}
          </span>
          <span className="shrink-0 text-[length:var(--text-micro)] text-[var(--text-faint)]">
            {c.sector.en}
          </span>
        </div>
        <div className="mt-0.5 flex items-baseline gap-2">
          <Raw
            lang="ar"
            className="min-w-0 flex-1 truncate text-[length:var(--text-data)] text-[var(--text-muted)]"
          >
            {c.entityNameAr ?? c.nameAr ?? ''}
          </Raw>
          <span className="shrink-0 text-[length:var(--text-micro)] text-[var(--text-faint)]">
            {c.industry && (v('industry', c.industry) ?? c.industry)}
          </span>
        </div>
      </Link>

      {/* --- what is here --------------------------------------------- */}
      <div className="tnum px-4 py-2.5 text-[length:var(--text-tiny)] text-[var(--text-muted)]">
        {tn('common.documents', c.documents.total, { n: count(c.documents.total, locale) })}
        {c.documents.years && (
          <>
            <Sep />
            {c.documents.years[0]}–{c.documents.years[1]}
          </>
        )}
        {c.documents.locales.length > 0 && (
          <>
            <Sep />
            {c.documents.locales.map((l) => v('locale', l) ?? l).join(', ')}
          </>
        )}
        {/*
          Deliberately in the *documents* row rather than next to the pass
          rate. Both are counts of documents, so they belong together; the
          pass rate is a property of the ones that were parsed and saying so
          in the same breath is what made "78% · 327 open" read as coverage.
        */}
        {superadmin && c.gaps.incomplete > 0 && (
          <>
            <Sep />
            <Link
              href={`/c/${c.symbol}?tab=gaps`}
              title={t('gaps.notParsedTitle', {
                missing: count(c.gaps.missing, locale),
                stalled: count(c.gaps.stalled, locale),
              })}
              className="link-inline text-[var(--fail)]"
            >
              {t('gaps.notParsed', { n: count(c.gaps.incomplete, locale) })}
            </Link>
          </>
        )}
      </div>

      {/* --- coverage -------------------------------------------------- */}
      <div className="flex flex-1 items-start px-4 pb-3">
        <CoverageMatrix symbol={c.symbol} coverage={c.coverage} />
      </div>

      {/* --- quality ---------------------------------------------------
          Hidden unless `?superadmin=1`. The fact count stays: it says how
          much was extracted, which is a property of the data. The pass rate
          and the open-review count say how well it *scored*, which is the
          whole of what this mode hides. */}
      {superadmin && <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1 border-t border-[var(--border)] px-4 py-2">
        <span className="tnum text-[length:var(--text-micro)] text-[var(--text-faint)]">
          {tn('common.facts', c.facts, { n: count(c.facts, locale) })}
        </span>
        <div className="flex-1" />
        <Bar value={c.quality.passRate} />
        <span
          className={cn(
            'tnum text-[length:var(--text-micro)]',
            band === 'pass' ? 'text-[var(--pass)]'
              : band === 'warn' ? 'text-[var(--warn)]'
              : band === 'fail' ? 'text-[var(--fail)]'
              : 'text-[var(--text-faint)]',
          )}
          title={t('quality.passRateOf', {
            pct: percent0(c.quality.passRate, locale),
            passed: count(c.quality.passed, locale),
            total: count(c.quality.checks, locale),
          })}
        >
          {t('results.passShort', { pct: percent0(c.quality.passRate, locale) })}
          <span className="ms-1 text-[var(--text-faint)]">
            {count(c.quality.passed, locale)}/{count(c.quality.checks, locale)}
          </span>
        </span>
        {c.quality.openReview > 0 && (
          <span className="tnum text-[length:var(--text-micro)] text-[var(--text-faint)]">
            {t('results.openReview', { n: count(c.quality.openReview, locale) })}
          </span>
        )}
      </div>}

      {/* Without the quality row the card loses its footer, so the fact
          count moves up rather than leaving the tile bottom-heavy. */}
      {!superadmin && (
        <div className="tnum border-t border-[var(--border)] px-4 py-2 text-[length:var(--text-micro)] text-[var(--text-faint)]">
          {tn('common.facts', c.facts, { n: count(c.facts, locale) })}
        </div>
      )}
    </article>
  )
}

const Sep = () => <span className="mx-1.5 text-[var(--border-strong)]">·</span>
